import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import './ClubsHub.css';

/* ─────────────────────────────────────────────────────────────
   CLUBS HUB PAGE — /clubs
   Shows the user's clubs, lets them join by code, browse public
   clubs by name, and create a new club.

   Auto-join flow:
   /clubs?join=<join_code> → silently joins and redirects to
   the club page. Mirrors the /friends?invite= flow.
   ───────────────────────────────────────────────────────────── */
function ClubsHub() {
  const { user }          = useAuth();
  const [searchParams]    = useSearchParams();
  const navigate          = useNavigate();

  // ── My clubs ───────────────────────────────────────────────
  const [myClubs,   setMyClubs]   = useState([]);
  const [loading,   setLoading]   = useState(true);

  // ── Join by code ───────────────────────────────────────────
  const [joinCode,      setJoinCode]      = useState('');
  const [joining,       setJoining]       = useState(false);
  const [joinError,     setJoinError]     = useState('');
  const [joinSuccess,   setJoinSuccess]   = useState('');

  // ── Browse public clubs ────────────────────────────────────
  const [searchQuery,   setSearchQuery]   = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [joiningClub,   setJoiningClub]   = useState(null); // club_id being joined via search

  // ── Load user's clubs on mount ─────────────────────────────
  const fetchMyClubs = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('club_memberships')
      .select('role, clubs(id, name, description, is_public)')
      .eq('user_id', user.id)
      .order('joined_at', { ascending: false });

    // Fetch member counts for each club
    const rows = data || [];
    if (!rows.length) {
      setMyClubs([]);
      setLoading(false);
      return;
    }

    const clubIds = rows.map(r => r.clubs.id);
    const { data: counts } = await supabase
      .from('club_memberships')
      .select('club_id')
      .in('club_id', clubIds);

    // Build a map of club_id → member count
    const countMap = {};
    (counts || []).forEach(c => {
      countMap[c.club_id] = (countMap[c.club_id] || 0) + 1;
    });

    const clubs = rows.map(r => ({
      ...r.clubs,
      role:         r.role,
      memberCount:  countMap[r.clubs.id] || 1,
    }));

    setMyClubs(clubs);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchMyClubs();
  }, [fetchMyClubs]);

  // ── Auto-join from ?join= param ────────────────────────────
  useEffect(() => {
    const code = searchParams.get('join');
    if (!code || !user) return;
    processJoinCode(code, true);
  }, [searchParams, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Debounced public clubs search ──────────────────────────
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    const timer = setTimeout(async () => {
      const { data } = await supabase.rpc('search_public_clubs', {
        p_query: searchQuery.trim(),
      });
      setSearchResults(data || []);
      setSearchLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ── Join club by code (shared logic) ──────────────────────
  async function processJoinCode(code, isAutoJoin = false) {
    if (!isAutoJoin) {
      // Strip the URL param immediately so a reload doesn't re-trigger
      navigate('/clubs', { replace: true });
    }
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;

    const { data, error } = await supabase.rpc('join_club_by_code', {
      p_join_code: trimmed,
    });

    if (error) {
      if (!isAutoJoin) {
        setJoinError(error.message || 'Invalid code. Please try again.');
      }
      return;
    }

    // Navigate to the club they just joined
    navigate('/clubs/' + data.club_id);
  }

  async function handleJoinByCode(e) {
    e.preventDefault();
    if (!joinCode.trim() || joining) return;
    setJoining(true);
    setJoinError('');
    setJoinSuccess('');
    await processJoinCode(joinCode);
    setJoining(false);
  }

  // ── Join from search results ───────────────────────────────
  async function handleJoinFromSearch(club) {
    setJoiningClub(club.club_id);
    const { data, error } = await supabase.rpc('join_club_by_code', {
      p_join_code: club.join_code,
    });
    if (error) {
      setJoiningClub(null);
      return;
    }
    navigate('/clubs/' + data.club_id);
  }

  return (
    <div className="ch-page">
      <div className="ch-container">

        {/* Page header */}
        <div className="ch-header-row">
          <h1 className="ch-title">Clubs</h1>
          <Link to="/clubs/new" className="btn-primary ch-create-btn">
            + Create Club
          </Link>
        </div>

        {/* ── MY CLUBS ──────────────────────────────────────── */}
        <section className="ch-section">
          <h2 className="ch-section-title">My Clubs</h2>

          {loading ? (
            <div className="ch-loading">
            <span className="ch-loading-star">★</span>
            <span className="ch-loading-star">★</span>
            <span className="ch-loading-star">★</span>
          </div>
          ) : myClubs.length === 0 ? (
            <div className="ch-empty">
              <p className="ch-empty-text">
                You're not in any clubs yet.
              </p>
              <p className="ch-empty-sub">
                Create one above, or join one using a code below.
              </p>
            </div>
          ) : (
            <div className="ch-clubs-grid">
              {myClubs.map(club => (
                <Link
                  key={club.id}
                  to={`/clubs/${club.id}`}
                  className="ch-club-card"
                >
                  <div className="ch-club-card-top">
                    <span className="ch-club-name">{club.name}</span>
                    <span className={`ch-role-badge${club.role === 'admin' ? ' ch-role-admin' : ''}`}>
                      {club.role === 'admin' ? 'Admin' : 'Member'}
                    </span>
                  </div>
                  {club.description && (
                    <p className="ch-club-desc">{club.description}</p>
                  )}
                  <div className="ch-club-meta">
                    <span className="ch-club-count">
                      {club.memberCount} {club.memberCount === 1 ? 'member' : 'members'}
                    </span>
                    <span className="ch-club-visibility">
                      {club.is_public ? '🌐 Public' : '🔒 Private'}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* ── JOIN BY CODE ──────────────────────────────────── */}
        <section className="ch-section">
          <h2 className="ch-section-title">Join a Club</h2>
          <p className="ch-section-sub">
            Have a join code or invite link? Enter it here.
          </p>
          <form className="ch-join-form" onSubmit={handleJoinByCode}>
            <input
              className="ch-join-input"
              type="text"
              placeholder="Enter join code (e.g. A1B2C3D4)"
              value={joinCode}
              onChange={e => {
                setJoinCode(e.target.value.toUpperCase());
                setJoinError('');
              }}
              maxLength={8}
              spellCheck={false}
              autoCapitalize="characters"
            />
            <button
              type="submit"
              className="btn-primary ch-join-btn"
              disabled={joining || !joinCode.trim()}
            >
              {joining ? 'Joining…' : 'Join'}
            </button>
          </form>
          {joinError   && <p className="ch-join-error">{joinError}</p>}
          {joinSuccess && <p className="ch-join-success">{joinSuccess}</p>}
        </section>

        {/* ── BROWSE PUBLIC CLUBS ──────────────────────────── */}
        <section className="ch-section">
          <h2 className="ch-section-title">Browse Public Clubs</h2>
          <p className="ch-section-sub">
            Search for public clubs to discover and join.
          </p>

          <div className="ch-search-wrap">
            <svg className="ch-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="ch-search-input"
              type="text"
              placeholder="Search clubs by name…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          {searchQuery.trim().length >= 2 && (
            <div className="ch-search-results">
              {searchLoading ? (
                <p className="ch-search-empty">Searching…</p>
              ) : searchResults.length === 0 ? (
                <p className="ch-search-empty">No public clubs found for "{searchQuery}".</p>
              ) : (
                searchResults.map(club => (
                  <div key={club.club_id} className="ch-search-row">
                    <div className="ch-search-info">
                      <span className="ch-search-name">{club.club_name}</span>
                      {club.description && (
                        <span className="ch-search-desc">{club.description}</span>
                      )}
                      <span className="ch-search-meta">
                        {club.member_count} {Number(club.member_count) === 1 ? 'member' : 'members'}
                      </span>
                    </div>
                    <button
                      className="btn-primary ch-search-join-btn"
                      onClick={() => handleJoinFromSearch(club)}
                      disabled={joiningClub === club.club_id}
                    >
                      {joiningClub === club.club_id ? 'Joining…' : 'Join'}
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </section>

      </div>
    </div>
  );
}

export default ClubsHub;
